import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import {
  ANNOUNCEMENT_ICONS,
  AnnouncementBannerConfig,
  AnnouncementIcon,
  useAnnouncementBanner,
  useUpdateAnnouncementBanner,
} from '@/hooks/useAnnouncementBanner';

const DEFAULTS: AnnouncementBannerConfig = {
  enabled: false,
  icon: 'Megaphone',
  text: '',
  cta_label: '',
  cta_url: '',
};

const ICON_MAP: Record<AnnouncementIcon, LucideIcon> = {
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
};

function PreviewBanner(c: AnnouncementBannerConfig) {
  const Icon = ICON_MAP[c.icon] ?? Megaphone;
  const hasCta = !!(c.cta_label && c.cta_url);
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 max-w-full">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <Icon className="h-4 w-4 text-primary shrink-0" />
      <span className="text-sm font-medium text-foreground truncate">{c.text}</span>
      {hasCta && (
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary shrink-0">
          {c.cta_label}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}

export default function AnnouncementBannerTab() {
  const { data, isLoading } = useAnnouncementBanner();
  const update = useUpdateAnnouncementBanner();
  const [form, setForm] = useState<AnnouncementBannerConfig>(DEFAULTS);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const save = () => update.mutate(form);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notificación destacada del header</CardTitle>
        <p className="text-sm text-muted-foreground">
          Aparece en la parte superior del sitio público (junto a los botones de Iniciar Sesión).
          Útil para webinars, lanzamientos o nuevos videos.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="font-medium">Mostrar notificación</Label>
            <p className="text-xs text-muted-foreground">
              Cuando está apagado, no se muestra nada.
            </p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Ícono</Label>
            <Select
              value={form.icon}
              onValueChange={(v) => setForm({ ...form, icon: v as AnnouncementIcon })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANNOUNCEMENT_ICONS.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Texto del anuncio</Label>
          <Input
            value={form.text}
            maxLength={100}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            placeholder="Ej: Webinar en vivo: IA aplicada a ventas"
          />
          <p className="text-xs text-muted-foreground mt-1">{form.text.length}/100</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Texto del botón (CTA)</Label>
            <Input
              value={form.cta_label}
              maxLength={30}
              onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
              placeholder="Ver ahora"
            />
          </div>
          <div>
            <Label>Enlace del botón (URL)</Label>
            <Input
              value={form.cta_url}
              onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
            Vista previa
          </p>
          {form.enabled && form.text ? (
            <div className="flex">
              <PreviewBanner {...form} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {form.enabled ? 'Agrega un texto para previsualizar.' : 'La notificación está desactivada.'}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
